
import { computed, defineComponent, onUnmounted, ref } from 'vue'
import { fetchDhMigrationQr, DhMigrationQrState } from '@/helpers/channels/dhMigration'
import {
    ReconnectChannel,
    fetchReconnectChannels,
    getChannelLabel,
    getChannelSteps,
    getReconnectCopy,
} from '@/helpers/channels/reconnectPrompt'

/** Тот же интервал, что у сайдбара: реже — клиент дольше смотрит на протухший QR. */
const QR_POLL_MS = 5000

export default defineComponent({
    name: 'ReconnectMigrationModal',
    props: {
        project: { type: Object, default: null },
    },
    setup(props) {
        const isOpen = ref(false)
        const channels = ref<ReconnectChannel[]>([])
        const doneIds = ref<string[]>([])
        const selectedId = ref<string | null>(null)
        const qrState = ref<DhMigrationQrState>({ status: 'waiting' })
        const pollTimer = ref<any>(null)

        const projectId = computed(() => String(props.project?.id ?? ''))
        const chatEnabled = computed(() => Boolean(props.project?.isDhChatEnabled))
        const copy = computed(() => getReconnectCopy(chatEnabled.value))
        const selected = computed(
            () => channels.value.find((c) => c.connectorId === selectedId.value) ?? null
        )
        const selectedLabel = computed(() =>
            selected.value ? getChannelLabel(selected.value.connectorType) : ''
        )
        const steps = computed(() =>
            selected.value ? getChannelSteps(selected.value.connectorType) : []
        )
        const doneCount = computed(() => doneIds.value.length)
        const allDone = computed(
            () => channels.value.length > 0 && doneCount.value === channels.value.length
        )

        const isDone = (channel: ReconnectChannel) => doneIds.value.includes(channel.connectorId)
        const label = (channel: ReconnectChannel) => getChannelLabel(channel.connectorType)

        const stopPolling = () => {
            if (pollTimer.value) {
                clearInterval(pollTimer.value)
                pollTimer.value = null
            }
        }

        const pollQr = async () => {
            const channel = selected.value
            if (!channel) return
            const result = await fetchDhMigrationQr(channel.connectorId)

            // Ручка не ответила (500, сеть, 409 от гейтвея) — молчим и ждём следующего
            // опроса, оставив QR на экране. Раньше это считалось успехом, и клиент
            // видел «Все каналы переподключены», ничего не переподключив (05.10).
            if (result.status === 'fallback' && result.reason === 'unavailable') {
                return
            }

            // Канал успели переподключить в другом месте (сайдбар, вторая вкладка) —
            // для модалки это такой же успех, как скан прямо здесь.
            if (result.status === 'authorized' || result.status === 'fallback') {
                if (!doneIds.value.includes(channel.connectorId)) {
                    doneIds.value = [...doneIds.value, channel.connectorId]
                }
                stopPolling()
                const next = channels.value.find((c) => !doneIds.value.includes(c.connectorId))
                if (next) {
                    selectChannel(next.connectorId)
                }
                return
            }
            qrState.value = result
        }

        const selectChannel = (connectorId: string) => {
            stopPolling()
            selectedId.value = connectorId
            qrState.value = { status: 'waiting' }
            pollQr()
            pollTimer.value = setInterval(pollQr, QR_POLL_MS)
        }

        const close = () => {
            stopPolling()
            isOpen.value = false
        }

        const maybeOpen = async () => {
            if (isOpen.value || !projectId.value) return

            const found = await fetchReconnectChannels(projectId.value)
            if (!found.length) return

            channels.value = found
            doneIds.value = []
            isOpen.value = true
            selectChannel(found[0].connectorId)
        }

        // immediate: проект обычно уже в сторе к моменту монтирования
        const stop = computed(() => projectId.value)
        const unwatch = (() => {
            let prev = ''
            return () => {
                if (stop.value && stop.value !== prev) {
                    prev = stop.value
                    maybeOpen()
                }
            }
        })()
        const timer = setInterval(unwatch, 300)
        unwatch()

        onUnmounted(() => {
            stopPolling()
            clearInterval(timer)
        })

        return {
            isOpen,
            channels,
            selectedId,
            selectedLabel,
            qrState,
            copy,
            chatEnabled,
            steps,
            doneCount,
            allDone,
            isDone,
            label,
            selectChannel,
            close,
        }
    },
})
